import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { Scan, Plus, X } from 'lucide-react';
import { RadiologyTest } from '../../types/api.types';
import { Modal } from '../common/Modal';
import { RadiologyTestForm } from '../radiologyTests/RadiologyTestForm';
import { useCreateRadiologyTest } from '../../hooks/useRadiologyTests';
import { CreateRadiologyTestDto } from '../../api/radiologyTests.api';
import { SearchableSelect } from '../common/SearchableSelect';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

interface RadiologyTestSelectorProps {
  radiologyTests: RadiologyTest[];
  selectedRadiologyTestIds: number[];
  onToggleRadiologyTest: (id: number) => void;
  onRadiologyTestAdded?: (radiologyTest: RadiologyTest) => void;
}

const RadiologyTestSelectorComponent: React.FC<RadiologyTestSelectorProps> = ({
  radiologyTests,
  selectedRadiologyTestIds,
  onToggleRadiologyTest,
  onRadiologyTestAdded,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [selectedRadiologyTestId, setSelectedRadiologyTestId] = useState<number | ''>('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const createMutation = useCreateRadiologyTest();

  const availableOptions = radiologyTests
    .filter((test) => !selectedRadiologyTestIds.includes(test.id))
    .map((test) => ({ value: test.id, label: test.name }));

  const handleSelectRadiologyTest = (value: string | number) => {
    const id = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!selectedRadiologyTestIds.includes(id)) {
      onToggleRadiologyTest(id);
    }
    setSelectedRadiologyTestId(''); // Reset dropdown to placeholder after selection
  };

  const handleAddRadiologyTest = async (data: CreateRadiologyTestDto) => {
    try {
      const newRadiologyTest = await createMutation.mutateAsync(data);
      toast.success(t('radiologyTests.radiologyTestCreated'));
      setIsAddModalOpen(false);
      if (onRadiologyTestAdded) {
        onRadiologyTestAdded(newRadiologyTest);
        onToggleRadiologyTest(newRadiologyTest.id);
      }
    } catch {
      toast.error(t('radiologyTests.radiologyTestCreatedError'));
    }
  };

  return (
    <div className="mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div
        className="rounded-3 p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 50%, #f9a8d4 100%)',
          border: '1px solid rgba(236, 72, 153, 0.3)',
          boxShadow: '0 4px 6px -1px rgba(236, 72, 153, 0.1), 0 2px 4px -1px rgba(236, 72, 153, 0.06)',
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="d-flex align-items-center gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                boxShadow: '0 4px 6px -1px rgba(236, 72, 153, 0.3)',
              }}
            >
              <Scan className="text-white" size={24} strokeWidth={2.5} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h3 className="mb-0 fw-bold" style={{ color: '#9f1239', fontSize: '1.25rem' }}>
                  {t('prescriptions.radiologyTests')}
                </h3>
                {radiologyTests.length > 0 && (
                  <span
                    className="badge"
                    style={{
                      background: 'rgba(236, 72, 153, 0.2)',
                      color: '#9f1239',
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                    }}
                  >
                    {radiologyTests.length} {t('prescriptions.available', 'متاح')}
                  </span>
                )}
              </div>
              <p className="mb-0 small" style={{ color: '#831843', opacity: 0.8 }}>
                {radiologyTests.length > 0
                  ? t('prescriptions.selectRadiologyFromDropdown', 'اختر من القائمة')
                  : t('radiologyTests.noRadiologyTestsAvailable', 'لا توجد أشعة متاحة. يرجى إضافة أشعة جديدة.')}
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex gap-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="flex-grow-1">
            <SearchableSelect
              placeholder={
                radiologyTests.length > 0
                  ? t('prescriptions.selectRadiologyTest', 'اختر أشعة...')
                  : t('common.noTestsAvailable', 'لا توجد أشعة متاحة')
              }
              value={selectedRadiologyTestId}
              onChange={(value) => {
                const id = value != null && value !== '' ? (typeof value === 'string' ? parseInt(value, 10) : value) : '';
                if (id !== '') handleSelectRadiologyTest(id);
              }}
              options={availableOptions}
              searchable
              disabled={radiologyTests.length === 0}
              clearable={true}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            className="d-flex align-items-center gap-2 fw-semibold"
            style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
              minWidth: '120px',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <Plus className="h-4 w-4" />
            {t('radiologyTests.addNewRadiologyTest', 'إضافة جديد')}
          </Button>
        </div>

        {radiologyTests.length === 0 && (
          <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded mt-3">
            <p className="text-warning mb-2 fw-semibold">
              {t('radiologyTests.noRadiologyTestsAvailable', 'لا توجد أشعة متاحة')}
            </p>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => setIsAddModalOpen(true)}
            >
              {t('radiologyTests.addItNow', 'أضفه الآن')}
            </button>
          </div>
        )}
      </div>

      {selectedRadiologyTestIds.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('prescriptions.selectedRadiologyTests')}</p>
          {selectedRadiologyTestIds.map((id) => {
            const radiologyTest = radiologyTests.find((r) => r.id === id);
            return (
              <div
                key={id}
                className="d-flex align-items-center justify-content-between bg-gray-50 p-3 rounded-lg"
              >
                <span className="fw-medium">{radiologyTest?.name ?? id}</span>
                <button
                  onClick={() => onToggleRadiologyTest(id)}
                  className="btn btn-link text-danger p-0"
                  type="button"
                  aria-label={t('common.remove', 'إزالة')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={t('radiologyTests.addNewRadiologyTest')}
      >
        <RadiologyTestForm
          onSubmit={handleAddRadiologyTest}
          isLoading={createMutation.isPending}
          onCancel={() => setIsAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export const RadiologyTestSelector = React.memo(RadiologyTestSelectorComponent);
